import { useState, useEffect, useCallback, useMemo } from "react";
import { C, S } from "../customers/theme";
import { getTemplates, createTemplate, updateTemplate, deleteTemplate, setDefaultTemplate, LAYOUT_STYLES, NEW_TEMPLATE } from "../lib/invoiceTemplates";
import { getBusinessSettings } from "../lib/businessSettings";
import InvoiceTemplatePreview from "./InvoiceTemplatePreview";

let _seq = 0;
function Field({ label, value, onChange, options, area }) {
  const id = useMemo(() => `it-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : area ? (
        <textarea id={id} style={{ ...S.input, height: 52 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
const Toggle = ({ label, checked, onChange }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", padding: "4px 0" }}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} /> {label}
  </label>
);

function TemplateEditor({ initial, business, onCancel, onSave }) {
  const isEdit = !!initial.id;
  const [t, setT] = useState({ ...NEW_TEMPLATE, ...initial });
  const [makeDefault, setMakeDefault] = useState(!!initial.is_default);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setT((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true); setError("");
    try { await onSave(t, makeDefault); }
    catch (e) { setError(e.message || "Could not save template"); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 960 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isEdit ? "Edit template" : "New template"}</h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 20, alignItems: "start" }}>
          <div>
            <Field label="Template name" value={t.name} onChange={set("name")} />
            <div style={{ marginTop: 10 }}><Field label="Layout style" value={t.layout_style} onChange={set("layout_style")} options={LAYOUT_STYLES} /></div>
            <div style={{ marginTop: 10 }}>
              <label style={S.label}>Accent colour</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={t.accent_color || C.primary} onChange={(e) => set("accent_color")(e.target.value)} aria-label="Accent colour" style={{ width: 44, height: 34, border: `1px solid ${C.border}`, borderRadius: 6, background: "none", cursor: "pointer" }} />
                <input style={S.input} value={t.accent_color || ""} onChange={(e) => set("accent_color")(e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
              <Toggle label="Show logo" checked={t.show_logo} onChange={set("show_logo")} />
              <Toggle label="Show bank details" checked={t.show_bank_details} onChange={set("show_bank_details")} />
              <Toggle label="Show terms / footer" checked={t.show_terms} onChange={set("show_terms")} />
              <Toggle label="Set as default template" checked={makeDefault} onChange={setMakeDefault} />
            </div>
            <div style={{ marginTop: 10 }}><Field label="Footer text" area value={t.footer_text} onChange={set("footer_text")} /></div>
            {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 12 }} role="alert">{error}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
              <button style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Create template"}</button>
            </div>
          </div>
          <div>
            <div style={{ ...S.label, marginBottom: 6 }}>Live preview</div>
            <InvoiceTemplatePreview template={t} business={business || {}} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceTemplateManager() {
  const [rows, setRows] = useState([]);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [{ data }, biz] = await Promise.all([getTemplates(), getBusinessSettings()]);
      setRows(data); setBusiness(biz);
    } catch (e) { setError(e.message || "Could not load templates."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (t, makeDefault) => {
    if (t.id) { await updateTemplate(t.id, t); if (makeDefault) await setDefaultTemplate(t.id); }
    else { await createTemplate(t, makeDefault); }
    setEditing(null);
    await load();
  };
  const makeDefault = async (t) => { try { await setDefaultTemplate(t.id); await load(); } catch (e) { setError(e.message); } };
  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await deleteTemplate(t.id); await load(); } catch (e) { setError(e.message); }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <div style={{ color: C.textMuted, fontSize: 13 }}>Define how your invoices look. The <strong style={{ color: C.text }}>default</strong> is used unless another is chosen.</div>
        <div style={{ flex: 1 }} />
        <button style={S.btn()} onClick={() => setEditing({ ...NEW_TEMPLATE })}>+ New Template</button>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 32 }}>
          <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No templates yet</div>
          <div style={{ color: C.textMuted, marginBottom: 12, fontSize: 13 }}>Create your first invoice template — it becomes your default automatically.</div>
          <button style={S.btn()} onClick={() => setEditing({ ...NEW_TEMPLATE })}>+ New Template</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {rows.map((t) => (
            <div key={t.id} style={{ ...S.card, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                {t.is_default && <span style={S.pill(C.success)}>Default</span>}
              </div>
              <div style={{ transform: "scale(1)", pointerEvents: "none", marginBottom: 10 }}>
                <InvoiceTemplatePreview template={t} business={business || {}} />
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button style={S.btn("secondary")} onClick={() => setEditing(t)}>Edit</button>
                {!t.is_default && <button style={S.btn("secondary")} onClick={() => makeDefault(t)}>Set default</button>}
                <button style={S.btn("ghost")} aria-label={`Delete ${t.name}`} onClick={() => del(t)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <TemplateEditor initial={editing} business={business} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
